const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN_X = 24;
const MARGIN_Y = 26;
const COLS = 3;
const ROWS = 8;
const LABEL_GAP = 6;
const LABELS_PER_PAGE = COLS * ROWS;
const LABEL_WIDTH = (PAGE_WIDTH - MARGIN_X * 2 - LABEL_GAP * (COLS - 1)) / COLS;
const LABEL_HEIGHT = (PAGE_HEIGHT - MARGIN_Y * 2 - LABEL_GAP * (ROWS - 1)) / ROWS;

function clean(value, max = 180) {
  return String(value ?? "").trim().slice(0, max);
}

function normalizeAscii(value) {
  return clean(value, 240)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "?");
}

function pdfEscape(value) {
  return normalizeAscii(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function barcodeValues(code) {
  const source = clean(code, 100);
  if (!source) return null;
  const values = [];
  for (const character of source) {
    const charCode = character.charCodeAt(0);
    if (charCode < 32 || charCode > 126) return null;
    values.push(charCode - 32);
  }
  const start = 104; // Code 128 B
  let checksum = start;
  values.forEach((value, index) => {
    checksum += value * (index + 1);
  });
  return [start, ...values, checksum % 103, 106];
}

function barcodeRuns(code) {
  const values = barcodeValues(code);
  if (!values) return null;
  const runs = [];
  let black = true;
  for (const value of values) {
    const pattern = CODE128_PATTERNS[value];
    for (const digit of pattern) {
      runs.push({ black, width: Number(digit) });
      black = !black;
    }
  }
  return runs;
}

function barcodeCommands(code, x, y, width, height) {
  const runs = barcodeRuns(code);
  if (!runs) return "";
  const modules = runs.reduce((sum, run) => sum + run.width, 0);
  const quietModules = 10;
  const moduleWidth = width / (modules + quietModules * 2);
  let cursor = x + quietModules * moduleWidth;
  const commands = [];
  for (const run of runs) {
    const runWidth = run.width * moduleWidth;
    if (run.black) {
      commands.push(`${cursor.toFixed(3)} ${y.toFixed(3)} ${runWidth.toFixed(3)} ${height.toFixed(3)} re f`);
    }
    cursor += runWidth;
  }
  return commands.join("\n");
}

function variantDescriptor(variant = {}) {
  const values = Object.values(variant?.attributes || {})
    .map((value) => clean(value, 60))
    .filter(Boolean);
  return values.join(" · ");
}

function productName(product = {}) {
  return clean(product.name || product.nombre || "Ítem", 100) || "Ítem";
}

function productCode(product = {}) {
  return clean(product.code || product.codigo || "", 100);
}

export function productBarcodeLabels(products = []) {
  const labels = [];
  const seen = new Set();

  const add = (label) => {
    const code = clean(label?.code, 100);
    if (!code || seen.has(code.toLocaleLowerCase("en-US"))) return;
    if (!barcodeValues(code)) return;
    seen.add(code.toLocaleLowerCase("en-US"));
    labels.push({
      code,
      title: clean(label?.title, 100) || "Ítem",
      detail: clean(label?.detail, 100),
    });
  };

  for (const product of Array.isArray(products) ? products : []) {
    const title = productName(product);
    const baseCode = productCode(product);
    if (baseCode) add({ code: baseCode, title, detail: "Código del ítem" });

    for (const variant of product.variants || product.variations || []) {
      if (variant?.active === false || variant?.activo === false) continue;
      const detail = variantDescriptor(variant);
      const barcode = clean(variant?.barcode, 100);
      const sku = clean(variant?.sku, 100);
      if (barcode) add({ code: barcode, title, detail: detail || "Código de variante" });
      if (sku && sku.toLocaleLowerCase("en-US") !== barcode.toLocaleLowerCase("en-US")) {
        add({ code: sku, title, detail: detail || "SKU de variante" });
      }
    }
  }

  return labels;
}

function pageContent(labels = []) {
  const commands = ["0 0 0 rg", "0.6 w"];
  labels.forEach((label, index) => {
    const col = index % COLS;
    const row = Math.floor(index / COLS);
    const x = MARGIN_X + col * (LABEL_WIDTH + LABEL_GAP);
    const top = PAGE_HEIGHT - MARGIN_Y - row * (LABEL_HEIGHT + LABEL_GAP);
    const y = top - LABEL_HEIGHT;

    commands.push(`0.82 0.84 0.86 RG ${x.toFixed(2)} ${y.toFixed(2)} ${LABEL_WIDTH.toFixed(2)} ${LABEL_HEIGHT.toFixed(2)} re S`);
    commands.push("0 0 0 rg");

    const title = pdfEscape(label.title).slice(0, 34);
    const detail = pdfEscape(label.detail).slice(0, 36);
    const code = pdfEscape(label.code).slice(0, 52);
    commands.push(`BT /F1 8 Tf ${(x + 8).toFixed(2)} ${(top - 13).toFixed(2)} Td (${title}) Tj ET`);
    if (detail) commands.push(`BT /F1 6.5 Tf ${(x + 8).toFixed(2)} ${(top - 23).toFixed(2)} Td (${detail}) Tj ET`);

    const barX = x + 8;
    const barY = y + 27;
    const barWidth = LABEL_WIDTH - 16;
    const barHeight = Math.max(30, LABEL_HEIGHT - 58);
    commands.push(barcodeCommands(label.code, barX, barY, barWidth, barHeight));
    commands.push(`BT /F1 7 Tf ${(x + 8).toFixed(2)} ${(y + 12).toFixed(2)} Td (${code}) Tj ET`);
  });
  return commands.filter(Boolean).join("\n");
}

function byteLength(value) {
  return new TextEncoder().encode(value).length;
}

export function buildBarcodeLabelsPdf(labels = []) {
  const cleanLabels = (Array.isArray(labels) ? labels : []).filter((label) => barcodeValues(label?.code));
  if (!cleanLabels.length) throw new Error("No hay códigos compatibles para generar etiquetas.");

  const pages = [];
  for (let index = 0; index < cleanLabels.length; index += LABELS_PER_PAGE) {
    pages.push(cleanLabels.slice(index, index + LABELS_PER_PAGE));
  }

  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };

  const catalogId = addObject("");
  const pagesId = addObject("");
  const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const pageIds = [];

  for (const labelsPage of pages) {
    const content = pageContent(labelsPage);
    const contentId = addObject(`<< /Length ${byteLength(content)} >>\nstream\n${content}\nendstream`);
    const pageId = addObject("");
    pageIds.push({ pageId, contentId });
  }

  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map(({ pageId }) => `${pageId} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;
  objects[catalogId - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;
  pageIds.forEach(({ pageId, contentId }) => {
    objects[pageId - 1] = `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE_WIDTH.toFixed(2)} ${PAGE_HEIGHT.toFixed(2)}] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`;
  });

  let pdf = "%PDF-1.4\n%WebProfit\n";
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(byteLength(pdf));
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = byteLength(pdf);
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index <= objects.length; index += 1) {
    pdf += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new TextEncoder().encode(pdf);
}

function safeFilename(value) {
  return clean(value, 80)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "codigos";
}

export function downloadBarcodeLabelsPdf(products = [], filename = "webprofit-codigos.pdf") {
  const labels = productBarcodeLabels(products);
  if (!labels.length) throw new Error("No hay códigos de ítem, SKU o barcode para descargar.");
  const bytes = buildBarcodeLabelsPdf(labels);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safeFilename(filename.replace(/\.pdf$/i, "")) + ".pdf";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { labels: labels.length };
}
